/** Timers are independent of the platform/refereeing clock. All values use milliseconds. */
export const BEHAVIOR_TIMER_EVENT = {
  snapshot: "behavior-timers:snapshot",
  command: "behavior-timers:command",
  request: "behavior-timers:request",
  publish: "behavior-timers:publish",
  status: "behavior-timers:status",
} as const;
export const TIMER_ACTIONS = [
  "timerStart",
  "timerStop",
  "timerResume",
  "timerReset",
  "timerSet",
  "timerDelete",
] as const;
export type TimerAction = (typeof TIMER_ACTIONS)[number];
export type TimerMode = "countdown" | "stopwatch";
export type TimerUnit = "ms" | "s" | "min";
export type TimerCommand = {
  action: TimerAction;
  reference: string;
  scope: string;
  valueMs?: number;
  mode?: TimerMode;
  unit?: TimerUnit;
};
export type TimerSnapshot = {
  reference: string;
  scope: string;
  mode: TimerMode;
  /** Display unit; older checkpoints default to milliseconds. */
  unit?: TimerUnit;
  /** UTC deadline retained after expiry; absent while paused. */
  deadlineAt?: number;
  initialMs: number;
  valueMs: number;
  running: boolean;
  finished: boolean;
  revision: number;
};
export type TimerPacket = {
  session?: string;
  sequence?: number;
  /** Monotonic server timestamps; account for persistence/network latency on clients. */
  sampledAtMs?: number;
  sentAtMs?: number;
  /** Whole snapshot on connect; only changed timers otherwise. */
  replace?: boolean;
  timers: TimerSnapshot[];
  removed?: { scope: string; reference: string }[];
};
export type TimerReading = TimerSnapshot & {
  exists: boolean;
  connected?: boolean;
  seconds: number;
  elapsedMs: number;
  remainingMs: number;
  formatted: string;
  formattedMs: string;
};
export type BehaviorTimerData = Record<string, TimerReading>;
/** Keep competition scopes isolated when the Electron registry hosts several meets. */
export function scopeTimerPacket(
  packet: TimerPacket,
  scope: string,
): TimerPacket {
  if (!packet || !Array.isArray(packet.timers) || packet.timers.length > 1024)
    throw new Error("BEHAVIOR_TIMER_INVALID_PACKET");
  return {
    ...packet,
    timers: packet.timers.filter((timer) => timer.scope === scope),
    removed: packet.removed?.filter((timer) => timer.scope === scope),
  };
}
export const isTimerAction = (key: string): key is TimerAction =>
  (TIMER_ACTIONS as readonly string[]).includes(key);
export const timerKey = (scope: string, reference: string) =>
  JSON.stringify([scope, reference]);
export function timerScope(data: {
  currentMeet?: unknown;
  behaviorTimerScope?: string;
}): string {
  const meet = data.currentMeet as { id?: unknown; guid?: unknown } | null;
  return data.behaviorTimerScope ?? String(meet?.guid ?? meet?.id ?? "default");
}
export function parseTimerCommand(value: unknown): TimerCommand {
  const c = value as Partial<TimerCommand> | null;
  if (
    !c ||
    !isTimerAction(String(c.action)) ||
    typeof c.reference !== "string" ||
    !c.reference.trim() ||
    c.reference.length > 128 ||
    typeof c.scope !== "string" ||
    !c.scope ||
    c.scope.length > 128
  )
    throw new Error("BEHAVIOR_TIMER_INVALID_REFERENCE");
  if (c.action === "timerStart" || c.action === "timerSet") {
    if (
      typeof c.valueMs !== "number" ||
      !Number.isFinite(c.valueMs) ||
      c.valueMs < 0 ||
      c.valueMs > 365 * 86400000
    )
      throw new Error("BEHAVIOR_TIMER_INVALID_DURATION");
  }
  if (c.mode !== undefined && c.mode !== "countdown" && c.mode !== "stopwatch")
    throw new Error("BEHAVIOR_TIMER_INVALID_MODE");
  if (c.unit !== undefined && !["ms", "s", "min"].includes(c.unit))
    throw new Error("BEHAVIOR_TIMER_INVALID_UNIT");
  return {
    action: c.action as TimerAction,
    reference: c.reference.trim(),
    scope: c.scope,
    valueMs: c.valueMs,
    mode: c.mode,
    unit: c.unit,
  };
}
export function timerCommandFromPorts(
  action: TimerAction,
  ports: Record<string, unknown>,
  scope: string,
): TimerCommand {
  const multiplier =
    ports.unit === "s"
      ? 1000
      : ports.unit === "min"
        ? 60000
        : ports.unit === "ms" || ports.unit === undefined
          ? 1
          : NaN;
  return parseTimerCommand({
    action,
    scope,
    reference: ports.reference,
    valueMs: Number(ports.duration ?? 0) * multiplier,
    mode: action === "timerStart" ? (ports.mode ?? "countdown") : undefined,
    unit: ["timerStart", "timerSet"].includes(action)
      ? (ports.unit ?? "ms")
      : undefined,
  });
}
function format(ms: number, precise: boolean, countdown: boolean): string {
  const total = precise
    ? Math.floor(ms)
    : (countdown ? Math.ceil(ms / 1000) : Math.floor(ms / 1000)) * 1000;
  const seconds = Math.floor(total / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}${precise ? `.${String(total % 1000).padStart(3, "0")}` : ""}`;
}
export function readTimer(snapshot?: TimerSnapshot, elapsed = 0): TimerReading {
  const s = snapshot ?? {
    reference: "",
    scope: "default",
    mode: "countdown",
    initialMs: 0,
    valueMs: 0,
    running: false,
    finished: false,
    revision: 0,
  };
  const valueMs = Math.max(
    0,
    s.valueMs +
      (s.running
        ? Math.max(0, elapsed) * (s.mode === "countdown" ? -1 : 1)
        : 0),
  );
  const finished =
    s.finished || (s.mode === "countdown" && s.running && valueMs === 0);
  return {
    ...s,
    exists: !!snapshot,
    valueMs,
    running: s.running && !finished,
    finished,
    seconds: valueMs / 1000,
    elapsedMs:
      s.mode === "stopwatch" ? valueMs : Math.max(0, s.initialMs - valueMs),
    remainingMs: s.mode === "countdown" ? valueMs : 0,
    formatted: format(valueMs, false, s.mode === "countdown"),
    formattedMs: format(valueMs, true, s.mode === "countdown"),
  };
}
export function timerOutput(
  data: BehaviorTimerData | undefined,
  reference: unknown,
  port: string,
): unknown {
  const key = String(reference ?? "").trim();
  const reading =
    data && Object.prototype.hasOwnProperty.call(data, key)
      ? data[key]
      : readTimer();
  if (port === "connected") return reading.connected === true;
  if (port === "unit") return reading.unit ?? "ms";
  if (port === "value") {
    const divisor =
      reading.unit === "s" ? 1000 : reading.unit === "min" ? 60000 : 1;
    const value = reading.valueMs / divisor;
    return reading.mode === "countdown" ? Math.ceil(value) : Math.floor(value);
  }
  return port === "output"
    ? reading.valueMs
    : port === "state"
      ? reading
      : reading[port as keyof TimerReading];
}
